import * as Sentry from '@sentry/react';

/**
 * Keeps React from crashing when something outside React has already moved the DOM node React is
 * about to remove or insert before.
 *
 * Browser translators (Chrome for iOS, Google Translate) and extensions rewrite text nodes React
 * owns. `#root` and `<body>` carry `translate="no"`, but in-app browsers and extensions ignore
 * it. On React's next commit, `removeChild` / `insertBefore` then throw NotFoundError ("The object
 * can not be found here", "not a child of this node"): SHOP-WEB-3 / -N / -P / -1B. The throw
 * escapes React's commit phase, and the customer ends up on the ErrorBoundary screen.
 *
 * When the parent does not match, the call becomes a no-op. A breadcrumb and a Sentry log line
 * keep the signal, so a real React bug that trips this guard still shows up in Logs.
 */
export function installDomMutationGuard(): void {
  if (typeof Node !== 'function' || !Node.prototype) {
    return;
  }

  const originalRemoveChild = Node.prototype.removeChild;
  const originalInsertBefore = Node.prototype.insertBefore;

  if ((originalRemoveChild as any).__domMutationGuard) {
    return;
  }

  const report = (operation: string, parent: Node, child: Node | null) => {
    const data = { operation, parent: parent.nodeName, child: child?.nodeName ?? null };

    Sentry.addBreadcrumb({ category: 'dom.guard', level: 'warning', data });
    Sentry.logger?.warn('dom_mutation_guard', data);
  };

  const removeChild = function <T extends Node>(this: Node, child: T): T {
    if (child.parentNode !== this) {
      report('removeChild', this, child);

      return child;
    }

    return originalRemoveChild.call(this, child) as T;
  };

  const insertBefore = function <T extends Node>(this: Node, newNode: T, referenceNode: Node | null): T {
    if (referenceNode && referenceNode.parentNode !== this) {
      report('insertBefore', this, referenceNode);

      return newNode;
    }

    return originalInsertBefore.call(this, newNode, referenceNode) as T;
  };

  (removeChild as any).__domMutationGuard = true;

  Node.prototype.removeChild = removeChild;
  Node.prototype.insertBefore = insertBefore;
}
